/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button } from '@adobe-commerce/elsie/components';
import '@/quote-management/components/ReferenceDocumentsList/ReferenceDocumentsList.css';

export interface ReferenceDocument {
  uid?: string;
  name: string;
  identifier?: string;
  url: string;
}

export interface ReferenceDocumentsListProps extends HTMLAttributes<HTMLDivElement> {
  documents: ReferenceDocument[];
  isEditable?: boolean;
  onAdd?: () => void;
  onEdit?: (document: ReferenceDocument) => void;
  onRemove?: (document: ReferenceDocument) => void;
}

export const ReferenceDocumentsList: FunctionComponent<ReferenceDocumentsListProps> = ({
  className,
  documents,
  isEditable = false,
  onAdd,
  onEdit,
  onRemove,
  ...props
}) => {
  const dictionary = useText({
    add: 'NegotiableQuoteTemplate.Manage.referenceDocuments.add',
    edit: 'NegotiableQuoteTemplate.Manage.referenceDocuments.edit',
    remove: 'NegotiableQuoteTemplate.Manage.referenceDocuments.remove',
    noReferenceDocuments: 'NegotiableQuoteTemplate.Manage.referenceDocuments.noReferenceDocuments',
  });

  const formatDocumentDisplay = (doc: ReferenceDocument): string => {
    if (doc.identifier) {
      return `${doc.name}, ${doc.identifier}`;
    }
    return `${doc.name} ${doc.url}`;
  };

  const handleDocumentClick = (e: Event, url: string) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={classes(['quote-management-reference-documents-list', className])}
      data-testid="reference-documents-list"
      {...props}
    >
      <div className="quote-management-reference-documents-list__content">
        {documents.map((doc) => (
          <div
            key={doc.uid || doc.url}
            className="quote-management-reference-documents-list__document"
            data-testid={`reference-document-${doc.uid || doc.url}`}
          >
            <a
              href={doc.url}
              onClick={(e) => handleDocumentClick(e, doc.url)}
              className="quote-management-reference-documents-list__document-link"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`reference-document-link-${doc.uid || doc.url}`}
            >
              {formatDocumentDisplay(doc)}
            </a>
            {isEditable && (
              <div className="quote-management-reference-documents-list__document-actions">
                <Button
                  variant="tertiary"
                  onClick={() => onEdit?.(doc)}
                  className="quote-management-reference-documents-list__edit-button"
                  data-testid={`reference-document-edit-${doc.uid || doc.url}`}
                >
                  {dictionary.edit}
                </Button>
                <span className="quote-management-reference-documents-list__separator">|</span>
                <Button
                  variant="tertiary"
                  onClick={() => onRemove?.(doc)}
                  className="quote-management-reference-documents-list__remove-button"
                  data-testid={`reference-document-remove-${doc.uid || doc.url}`}
                >
                  {dictionary.remove}
                </Button>
              </div>
            )}
          </div>
        ))}
        {isEditable && (
          <Button
            variant="tertiary"
            onClick={onAdd}
            className="quote-management-reference-documents-list__add-button"
            data-testid="reference-documents-add-button"
          >
            + {dictionary.add}
          </Button>
        )}
        {documents.length === 0 && !isEditable && (
          <div className="quote-management-reference-documents-list__empty">
            {dictionary.noReferenceDocuments}
          </div>
        )}
      </div>
    </div>
  );
};

